import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { fetchWithAuth } from '../../utils/api';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useSharePortfolio(isOpen, onClose) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [expandedScope, setExpandedScope] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientRole, setRecipientRole] = useState('');
  const [scope, setScope] = useState('full');
  const [selectedAssetClasses, setSelectedAssetClasses] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [allowPdfView, setAllowPdfView] = useState(false);
  const [allowSummaryExport, setAllowSummaryExport] = useState(false);
  const [branding, setBranding] = useState('myinvestfolio');
  const [durationDays, setDurationDays] = useState(7);
  const [customExpiry, setCustomExpiry] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchWithAuth(`${BACKEND_URL}/api/entities`).then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setEntities(Array.isArray(data) ? data : data.entities || []); })
        .catch(e => logger.error('Failed to load entities:', e));
    }
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setStep(1); setRecipientName(''); setRecipientEmail(''); setRecipientRole('');
    setScope('full'); setSelectedAssetClasses([]); setSelectedEntities([]);
    setAllowPdfView(false); setAllowSummaryExport(false); setBranding('myinvestfolio');
    setDurationDays(7); setCustomExpiry(''); setExpandedScope(null);
  }, []);

  const handleClose = useCallback(() => { resetForm(); onClose(); }, [resetForm, onClose]);

  const canProceed = () => {
    if (step === 1) return recipientName && recipientEmail && recipientRole;
    if (step === 2) { if (scope === 'asset_classes') return selectedAssetClasses.length > 0; if (scope === 'entities') return selectedEntities.length > 0; return true; }
    if (step === 4 && durationDays === 'custom') return !!customExpiry;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const body = { recipient_name: recipientName, recipient_email: recipientEmail, recipient_role: recipientRole, scope, allow_pdf_view: allowPdfView, allow_summary_export: allowSummaryExport, branding, duration_days: durationDays === 'custom' ? 0 : durationDays };
      if (scope === 'asset_classes') body.scope_details = { asset_classes: selectedAssetClasses };
      else if (scope === 'entities') body.scope_details = { entity_ids: selectedEntities };
      if (durationDays === 'custom' && customExpiry) body.custom_expiry = new Date(customExpiry).toISOString();
      const res = await fetchWithAuth(`${BACKEND_URL}/api/portfolio/shares`, { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) { toast.success('Portfolio access granted successfully.'); handleClose(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to create share.'); }
    } catch (e) { toast.error('An error occurred. Please try again.'); }
    finally { setLoading(false); }
  };

  const toggleAssetClass = (cls) => setSelectedAssetClasses(prev => prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]);
  const toggleEntity = (entityId) => setSelectedEntities(prev => prev.includes(entityId) ? prev.filter(e => e !== entityId) : [...prev, entityId]);

  return {
    step, setStep, loading, entities, expandedScope, setExpandedScope,
    recipientName, setRecipientName, recipientEmail, setRecipientEmail,
    recipientRole, setRecipientRole, scope, setScope,
    selectedAssetClasses, selectedEntities, allowPdfView, setAllowPdfView,
    allowSummaryExport, setAllowSummaryExport, branding, setBranding,
    durationDays, setDurationDays, customExpiry, setCustomExpiry,
    handleClose, canProceed, handleSubmit, toggleAssetClass, toggleEntity,
  };
}
